import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import { withSessionRecovery } from '@/services/session-recovery'
import type { ApiResponse } from '@/types'

export interface CatalogRole {
  id: string
  _id?: string | number | null
  name: string
  title?: string | null
  code?: string | null
  unique_id?: string
  role_group?: string | number | null
  role_group_id?: string | number | null
  role_group_name?: string | null
  role_level?: number | null
  role_level_code?: string | null
  role_level_label?: string | null
  default_role?: boolean
  primary_role?: boolean
  max_assignment_count?: number | null
  worker_skill_count?: number | string | null
  level_variant?: number | null
  level_variant_unique_id?: string | null
  level_variant_name?: string | null
  service_instance?: string | null
  service_instance_name?: string | null
  service_variant?: string | number | null
  service_variant_name?: string | null
  scope_type?: 'organization' | 'service_instance' | 'system' | null
  active?: boolean | number
  is_system?: boolean
  is_catalog_role?: boolean
  created_on?: string | null
  updated_on?: string | null
}

export interface CatalogRoleGroup {
  id: string | number
  encrypted_id?: string
  title: string
  service_instance_id?: string | null
  service_instance_name?: string | null
  service_variant?: string | number | null
  service_variant_name?: string | null
  scope_type?: 'organization' | 'service_instance' | 'system' | null
  is_system?: boolean
  status?: string | null
}

type CatalogRoleListParams = {
  serviceInstanceId?: string | null
  serviceVariant?: string | number | null
  includeHidden?: boolean
  kind?: 'job' | 'event' | 'committee' | 'task'
}

type CatalogRoleGroupListParams = {
  serviceInstanceId?: string | null
  serviceVariant?: string | number | null
}

type CatalogRoleListPayload =
  | CatalogRole[]
  | {
      data?: CatalogRole[]
      results?: CatalogRole[]
    }

type CatalogRoleGroupListPayload =
  | CatalogRoleGroup[]
  | {
      data?: CatalogRoleGroup[]
      results?: CatalogRoleGroup[]
    }

type CatalogRoleRef = {
  skill_role?: string | number | null
  skill_role_unique_id?: string | null
  skill_role_name?: string | null
}

/** Map a stored skill-role FK to the catalog role id used by role pickers. */
export function resolveCatalogRoleSelectId(
  roleRef: CatalogRoleRef,
  roles: CatalogRole[],
): string {
  const numericRoleId = String(roleRef.skill_role ?? '').trim()
  if (!numericRoleId && !roleRef.skill_role_unique_id && !roleRef.skill_role_name) {
    return ''
  }

  if (numericRoleId && roles.some((role) => String(role.id) === numericRoleId)) {
    return numericRoleId
  }

  const uniqueId = String(roleRef.skill_role_unique_id ?? '').trim()
  if (uniqueId) {
    const byUnique = roles.find((role) => String(role.unique_id ?? '').trim() === uniqueId)
    if (byUnique?.id != null) {
      return String(byUnique.id)
    }
  }

  const roleName = String(roleRef.skill_role_name ?? '').trim()
  if (roleName) {
    const byName = roles.find((role) => String(role.name ?? '').trim() === roleName)
    if (byName?.id != null) {
      return String(byName.id)
    }
  }

  return numericRoleId
}

type CatalogRoleMutationPayload = {
  role: string
  role_group?: string | number
  service_variant?: string | number
  service_instance?: string | number
  apply_to_organization?: 0 | 1
  is_system?: 0 | 1
  primary_role?: 0 | 1
  role_level?: number
  max_assignment_count?: number
  level_variant?: number
  active?: 0 | 1
  assignment_kind_key?: 'job' | 'event' | 'committee' | 'task'
}

type CatalogRoleGroupMutationPayload = {
  title: string
  service_variant?: string | number
  org_instance?: string | number
}

const extractPayload = <T>(payload: ApiResponse<T> | T): T => (
  typeof payload === 'object' && payload !== null && 'data' in payload
    ? payload.data
    : payload
) as T

const normalizeRoles = (payload?: CatalogRoleListPayload): CatalogRole[] => {
  if (!payload) {
    return []
  }

  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload.results)) {
    return payload.results
  }

  if ('data' in payload && Array.isArray(payload.data)) {
    return payload.data
  }

  return []
}

const normalizeGroups = (payload?: CatalogRoleGroupListPayload): CatalogRoleGroup[] => {
  if (!payload) {
    return []
  }

  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload.results)) {
    return payload.results
  }

  if ('data' in payload && Array.isArray(payload.data)) {
    return payload.data
  }

  return []
}

export const catalogRolesApi = {
  list: async (params?: CatalogRoleListParams): Promise<CatalogRole[]> => withSessionRecovery(async () => {
    const endpoint = params?.kind === 'event'
      ? API_PATHS.scopedRoles.eventList
      : params?.kind === 'committee'
        ? API_PATHS.scopedRoles.committeeList
        : params?.kind === 'task'
          ? API_PATHS.scopedRoles.taskList
          : API_PATHS.scopedRoles.jobsList
    const response = await api.get<ApiResponse<CatalogRoleListPayload> | CatalogRoleListPayload>(
      endpoint,
      {
        params: {
          page_size: 200,
          kind: params?.kind || undefined,
          service_instance_id: params?.serviceInstanceId || undefined,
          service_variant: params?.serviceVariant || undefined,
          include_hidden: params?.includeHidden ? 1 : undefined,
        },
      },
    )
    return normalizeRoles(extractPayload(response.data))
  }),

  listGroups: async (params?: CatalogRoleGroupListParams): Promise<CatalogRoleGroup[]> => withSessionRecovery(async () => {
    const response = await api.get<ApiResponse<CatalogRoleGroupListPayload> | CatalogRoleGroupListPayload>(
      API_PATHS.catalogRoles.groupsList,
      {
        params: {
          page_size: 200,
          service_instance_id: params?.serviceInstanceId || undefined,
          service_variant: params?.serviceVariant || undefined,
        },
      },
    )
    return normalizeGroups(extractPayload(response.data))
  }),

  create: async (payload: CatalogRoleMutationPayload): Promise<CatalogRole> => withSessionRecovery(async () => {
    const response = await api.post<ApiResponse<CatalogRole> | CatalogRole>(API_PATHS.catalogRoles.create, payload)
    return extractPayload(response.data)
  }),

  get: async (id: string | number): Promise<CatalogRole> => withSessionRecovery(async () => {
    const response = await api.get<ApiResponse<CatalogRole> | CatalogRole>(API_PATHS.catalogRoles.get(id))
    return extractPayload(response.data)
  }),

  update: async (id: string | number, payload: CatalogRoleMutationPayload): Promise<CatalogRole> => withSessionRecovery(async () => {
    const response = await api.patch<ApiResponse<CatalogRole> | CatalogRole>(API_PATHS.catalogRoles.update(id), payload)
    return extractPayload(response.data)
  }),

  /** Deactivates a catalog role (backend does not hard-delete catalog roles). */
  deactivate: async (id: string | number): Promise<void> => withSessionRecovery(async () => {
    await api.delete(API_PATHS.catalogRoles.delete(id))
  }),

  createGroup: async (payload: CatalogRoleGroupMutationPayload): Promise<CatalogRoleGroup> => withSessionRecovery(async () => {
    const response = await api.post<ApiResponse<CatalogRoleGroup> | CatalogRoleGroup>(
      API_PATHS.catalogRoles.groupsCreate,
      payload,
    )
    return extractPayload(response.data)
  }),

  updateGroup: async (id: string | number, payload: CatalogRoleGroupMutationPayload): Promise<CatalogRoleGroup> => withSessionRecovery(async () => {
    const response = await api.patch<ApiResponse<CatalogRoleGroup> | CatalogRoleGroup>(
      API_PATHS.catalogRoles.groupsUpdate(id),
      payload,
    )
    return extractPayload(response.data)
  }),

  removeGroup: async (id: string | number): Promise<void> => withSessionRecovery(async () => {
    await api.delete(API_PATHS.catalogRoles.groupsDelete(id))
  }),
}
